import { Loader } from "@/components/shared";
import { getMultipleUsersByIds } from "@/lib/appwrite/api";
import { useGetCurrentUser, useGetUserById } from "@/lib/react-query/queries";
import { IUser, DealDocument } from "@/types";
import { useEffect, useState } from "react";
import ChatList from "./ChatList";
import MessageView from "./MessageView";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

type DealWithUser = DealDocument & {
  otherUser?: {
    id: string;
    name: string;
    imageUrl?: string;
  };
};

const ChatContainer = () => {
  const { data: currentUser } = useGetCurrentUser();
  const currentUserId = currentUser?.$id || currentUser?.id || "";
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const conversationUser1 = searchParams.get('conversation');
  const conversationUser2 = searchParams.get('with');
  const postId = searchParams.get('postId');
  const dealId = searchParams.get('dealId');
  const { data: targetUser } = useGetUserById(id || "");
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const navigate = useNavigate();

  // Reset selected user when navigating to /chats
  useEffect(() => {
    if (!id && !conversationUser1 && !conversationUser2) {
      setSelectedUser(null);
    }
  }, [id, conversationUser1, conversationUser2]);

  // Auto-select logic to handle both regular users and conversation pairs
  useEffect(() => {
    const handleUserSelection = async () => {
      if (conversationUser1 && conversationUser2) {
        // This is a conversation pair for agent monitoring
        try {
          const users = await getMultipleUsersByIds([conversationUser1, conversationUser2]);
          const usersById = new Map(users.map((user: any) => [user.$id ?? user.id, user]));
          const rawUser1 = usersById.get(conversationUser1) as any;
          const rawUser2 = usersById.get(conversationUser2) as any;
          const user1 = {
            ...rawUser1,
            id: conversationUser1,
            $id: conversationUser1,
            name: rawUser1?.name || rawUser1?.displayName || rawUser1?.username || "Buyer",
          };
          const user2 = {
            ...rawUser2,
            id: conversationUser2,
            $id: conversationUser2,
            name: rawUser2?.name || rawUser2?.displayName || rawUser2?.username || "Seller",
          };

          const conversationPair: any = {
            id: `${conversationUser1}-${conversationUser2}`,
            $id: `${conversationUser1}-${conversationUser2}`,
            name: `${user1.name} ↔ ${user2.name}`,
            username: `${user1.username || "buyer"}_${user2.username || "seller"}`,
            emailOrPhone: '',
            bio: `Conversation between ${user1.name} and ${user2.name}`,
            imageUrl: '',
            acceptedSLA: true,
            isConversation: true,
            participants: [user1, user2]
          };
          setSelectedUser(conversationPair);
        } catch (error) {
          console.error('Error fetching conversation participants:', error);
          navigate('/chats');
        }
      } else if (id && targetUser) {
        // Regular user chat
        try {
          setSelectedUser({
            id: targetUser.$id,
            name: targetUser.name,
            username: targetUser.username,
            emailOrPhone: targetUser.emailOrPhone,
            bio: targetUser.bio || '',
            imageUrl: targetUser.imageUrl || '',
            acceptedSLA: targetUser.acceptedSLA
          });
        } catch (error) {
          navigate('/chats');
        }
      }
    };

    handleUserSelection();
  }, [id, targetUser, conversationUser1, conversationUser2, navigate]);

  // Handle deal selection from ChatList
  const handleSelectDeal = (deal: DealWithUser) => {
    if (currentUser?.isSupportAgent) {
      const params = new URLSearchParams({
        conversation: deal.buyerId,
        with: deal.sellerId,
        dealId: deal.$id,
      });
      if (deal.postId) params.set('postId', deal.postId);
      navigate(`/chats?${params.toString()}`);
      return;
    }
    const otherUserId = deal.otherUser?.id;
    if (otherUserId) {
      // Navigate with dealId and postId for proper context
      const params = new URLSearchParams();
      if (deal.postId) params.set('postId', deal.postId);
      if (deal.$id) params.set('dealId', deal.$id);

      const queryString = params.toString();
      navigate(`/chats/${otherUserId}${queryString ? `?${queryString}` : ''}`);
    }
  };

  if (!currentUser) return <Loader />;

  return (
    <div className="flex flex-col w-full min-h-full flex-1 bg-gray-900">
      {selectedUser ? (
        <MessageView
          selectedUser={selectedUser}
          currentUserId={currentUserId}
          onBack={() => navigate('/chats')}
          postId={postId || undefined}
          dealId={dealId || undefined}
        />
      ) : (
        <ChatList onSelectDeal={handleSelectDeal} />
      )}
    </div>
  );
};

export default ChatContainer;
